import { CalendarType } from '../models/calendar'

interface QuickTipConfig {
  translationKey: string
  includeCalendarType?: CalendarType[]
  excludeCalendarType?: CalendarType[]
}

const quickTips: Record<string, QuickTipConfig[]> = {
  'meeting-details': [
    {
      translationKey: 'quick_tips.meeting_details.clear_name_and_description',
    },
    {
      translationKey: 'quick_tips.meeting_details.groups_share_link',
    },
  ],
  'staff-location': [
    {
      translationKey: 'quick_tips.staff_location.custom_location',
    },
    {
      translationKey: 'quick_tips.staff_location.ask_booker_location',
      includeCalendarType: [
        CalendarType.EVENT,
        CalendarType.PERSONAL,
        CalendarType.ROUND_ROBIN,
      ],
    },
    {
      translationKey: 'quick_tips.staff_location.round_robin_priority',
      includeCalendarType: [CalendarType.ROUND_ROBIN],
    },
    {
      translationKey: 'quick_tips.staff_location.connect_video_integrations',
      excludeCalendarType: [CalendarType.EVENT, CalendarType.CLASS_BOOKING],
    },
  ],
  availability: [
    {
      translationKey: 'quick_tips.availability.staff_availability_required',
      excludeCalendarType: [CalendarType.EVENT],
    },
    {
      translationKey: 'quick_tips.availability.use_precreated_schedule',
      excludeCalendarType: [CalendarType.EVENT],
    },
    {
      translationKey: 'quick_tips.availability.timezone_in_schedule',
      excludeCalendarType: [CalendarType.EVENT],
    },
    {
      translationKey: 'quick_tips.availability.event_24_hours',
      includeCalendarType: [CalendarType.EVENT],
    },
  ],
  'booking-rules': [
    {
      translationKey: 'quick_tips.booking_rules.apply_to_all_meetings',
    },
    {
      translationKey: 'quick_tips.booking_rules.minimum_scheduling_notice',
    },
    {
      translationKey: 'quick_tips.booking_rules.maximum_bookings_per_slot',
    },
  ],
  forms: [
    {
      translationKey: 'quick_tips.forms.custom_contact_fields',
    },
    {
      translationKey: 'quick_tips.forms.create_custom_form',
    },
  ],
  payments: [
    {
      translationKey: 'quick_tips.payments.collected_during_booking',
    },
    {
      translationKey: 'quick_tips.payments.payment_description',
    },
  ],
  notifications: [
    {
      translationKey: 'quick_tips.notifications.review_policies',
    },
    {
      translationKey: 'quick_tips.notifications.use_workflows',
    },
  ],
  customizations: [
    {
      translationKey: 'quick_tips.customizations.preview_widget',
    },
    {
      translationKey: 'quick_tips.customizations.update_widget_language',
    },
  ],
  connections: [
    {
      translationKey: 'quick_tips.connections.google_organic_booking',
      excludeCalendarType: [CalendarType.EVENT],
    },
    {
      translationKey: 'quick_tips.connections.external_sync_event',
      includeCalendarType: [CalendarType.EVENT],
    },
  ],
  equipments: [
    {
      translationKey: 'quick_tips.equipments.room_definition',
    },
    {
      translationKey: 'quick_tips.equipments.equipment_definition',
    },
  ],
}

const quickTipCache = new Map<string, number>()

const getDeterministicIndex = (
  key: string,
  calendarType: CalendarType | undefined,
  length: number
): number => {
  const cacheKey = `${key}-${calendarType || 'undefined'}`

  // If we already have a cached index for this combination, use it
  if (quickTipCache.has(cacheKey)) {
    return quickTipCache.get(cacheKey)!
  }

  // Otherwise, generate a deterministic index using a simple hash
  let hash = 0
  for (let i = 0; i < cacheKey.length; i++) {
    const char = cacheKey.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  const index = Math.abs(hash) % length
  quickTipCache.set(cacheKey, index)

  return index
}

export const getQuickTip = (
  key: string,
  calendarType: CalendarType | undefined,
  t: (translationKey: string) => string
) => {
  const quickTipConfigs = quickTips[key] ?? quickTips['meeting-details']

  const availableTips = quickTipConfigs.filter(tip => {
    if (tip.includeCalendarType) {
      if (calendarType && tip.includeCalendarType.includes(calendarType)) {
        return true
      }
      return false
    }
    if (tip.excludeCalendarType) {
      if (!calendarType || !tip.excludeCalendarType.includes(calendarType)) {
        return true
      }
      return false
    }
    return true
  })

  const quickTipsLength = availableTips.length
  if (!quickTipsLength) {
    return ''
  }

  // Use deterministic selection instead of random
  const deterministicIndex = getDeterministicIndex(
    key,
    calendarType,
    quickTipsLength
  )
  const selectedTip = availableTips[deterministicIndex]

  return selectedTip ? t(selectedTip.translationKey) : ''
}
